import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Upload, FileText, MapPin, Mail, Phone } from "lucide-react";
import Section, { SectionHeading, SectionLabel } from "@/components/Section";
import { submitContact, submitApplication } from "@/lib/api";

const REQUIREMENTS = [
  "Dark Store Management",
  "Manpower Deployment",
  "QC Marketplace Management",
  "Warehousing & Fulfilment",
  "Supply Chain Support",
  "Careers",
  "Other",
];

function useQuery() {
  const { search } = useLocation();
  return new URLSearchParams(search);
}

export default function Contact() {
  const q = useQuery();
  const prefRole = q.get("role") || "";
  const prefType = q.get("type") || "";
  const isCareers = prefType === "Careers" || !!prefRole;

  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", company: "", designation: "", email: "", phone: "",
    city: "", requirement_type: isCareers ? "Careers" : "", message: prefRole ? `Applying for: ${prefRole}` : "",
  });

  const [careerForm, setCareerForm] = useState({
    name: "", email: "", phone: "", role: prefRole, city: "", message: "",
  });
  const [resume, setResume] = useState(null);

  useEffect(() => {
    if (prefRole) setCareerForm((f) => ({ ...f, role: prefRole }));
  }, [prefRole]);

  const onContactChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onCareerChange = (k) => (e) => setCareerForm({ ...careerForm, [k]: e.target.value });

  const onContactSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.requirement_type) {
      toast.error("Please fill name, email, phone and requirement.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitContact(form);
      toast.success(res.message || "Submitted. We’ll be in touch.");
      setForm({ name: "", company: "", designation: "", email: "", phone: "", city: "", requirement_type: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const onCareerSubmit = async (e) => {
    e.preventDefault();
    if (!careerForm.name || !careerForm.email || !careerForm.phone || !careerForm.role) {
      toast.error("Please fill name, email, phone and role.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(careerForm).forEach(([k, v]) => { if (v != null) fd.append(k, v); });
      if (resume) fd.append("resume", resume);
      const res = await submitApplication(fd);
      toast.success(res.message || "Application received.");
      setCareerForm({ name: "", email: "", phone: "", role: prefRole, city: "", message: "" });
      setResume(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Application failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Dark form inputs (because card is dark)
  const inputCls = "w-full rounded-2xl bg-white/[0.06] border border-white/15 text-white placeholder:text-white/40 px-4 py-3.5 text-sm focus:border-[#99CC00] focus:bg-white/[0.10] focus:outline-none transition-colors";
  const labelCls = "block text-[11px] font-bold uppercase tracking-[0.18em] text-white/55 mb-2";

  return (
    <div data-testid="page-contact" className="pt-[72px]">
      <Section id="contact-hero" bg="soft">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow={isCareers ? "Careers Application" : "Partner With OneHill"}
              title={isCareers
                ? <>Apply for <span className="text-[#99CC00]">{prefRole || "an open role"}.</span></>
                : <>Ready to expand your <span className="text-[#99CC00]">quick commerce</span> operations?</>}
              sub={isCareers
                ? "Send us your details and resume. Our HR team will get back within 5 working days."
                : "Tell us your operating goals. We’ll come back with a tailored plan within 48 hours."}
            />

            <div className="mt-10 space-y-4 text-sm">
              <div className="flex items-start gap-3 text-neutral-700"><MapPin className="w-4 h-4 text-[#99CC00] mt-0.5 flex-shrink-0" /> The Platina Tower, 6th Floor, MG Road, Gurgaon, Haryana — 122002</div>
              <div className="flex items-center gap-3 text-neutral-700"><Mail className="w-4 h-4 text-[#99CC00] flex-shrink-0" /> <a href="mailto:sarthak@onehill.in" className="hover:text-[#99CC00] transition-colors">sarthak@onehill.in</a></div>
              <div className="flex items-center gap-3 text-neutral-700"><Phone className="w-4 h-4 text-[#99CC00] flex-shrink-0" /> <a href="tel:+919953800053" className="hover:text-[#99CC00] transition-colors">+91 99538 00053</a></div>
            </div>

            <div className="mt-10 rounded-3xl border border-neutral-200 bg-white p-6">
              <div className="text-xs font-mono uppercase tracking-[0.22em] text-[#99CC00] font-black">Response SLA</div>
              <p className="mt-2 text-sm text-neutral-600">All partner enquiries are routed to our operations leadership. Expect a reply within 48 working hours.</p>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7"
          >
            <div className="rounded-[2rem] bg-[#1F1F1F] text-white p-7 lg:p-10 shadow-xl">
              <div className="flex items-center justify-between mb-7">
                <SectionLabel>{isCareers ? "Form · Application" : "Form · Partnership"}</SectionLabel>
                <div className="text-[10px] font-mono text-white/50 uppercase tracking-[0.18em]">Encrypted</div>
              </div>

              {!isCareers ? (
                <form
                  data-testid="contact-form"
                  className="grid grid-cols-1 sm:grid-cols-2 gap-5"
                  onSubmit={onContactSubmit}
                >
                  <div>
                    <label className={labelCls}>Name *</label>
                    <input data-testid="contact-name" required value={form.name} onChange={onContactChange("name")} className={inputCls} placeholder="Your name" />
                  </div>
                  <div>
                    <label className={labelCls}>Company</label>
                    <input data-testid="contact-company" value={form.company} onChange={onContactChange("company")} className={inputCls} placeholder="Company name" />
                  </div>
                  <div>
                    <label className={labelCls}>Designation</label>
                    <input data-testid="contact-designation" value={form.designation} onChange={onContactChange("designation")} className={inputCls} placeholder="Role / title" />
                  </div>
                  <div>
                    <label className={labelCls}>Email *</label>
                    <input data-testid="contact-email" required type="email" value={form.email} onChange={onContactChange("email")} className={inputCls} placeholder="you@company.com" />
                  </div>
                  <div>
                    <label className={labelCls}>Phone *</label>
                    <input data-testid="contact-phone" required value={form.phone} onChange={onContactChange("phone")} className={inputCls} placeholder="+91 — Phone" />
                  </div>
                  <div>
                    <label className={labelCls}>City</label>
                    <input data-testid="contact-city" value={form.city} onChange={onContactChange("city")} className={inputCls} placeholder="City" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Requirement Type *</label>
                    <select
                      data-testid="contact-requirement"
                      required
                      value={form.requirement_type}
                      onChange={onContactChange("requirement_type")}
                      className={inputCls + " appearance-none cursor-pointer"}
                    >
                      <option value="" disabled className="bg-[#1F1F1F] text-white">Select a requirement</option>
                      {REQUIREMENTS.map((r) => (
                        <option key={r} value={r} className="bg-[#1F1F1F] text-white">{r}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Message</label>
                    <textarea data-testid="contact-message" rows={4} value={form.message} onChange={onContactChange("message")} className={inputCls + " resize-none"} placeholder="Tell us about your operations / expansion goals" />
                  </div>
                  <div className="sm:col-span-2 flex items-center justify-end gap-3 pt-1">
                    <button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={submitting}
                      className="inline-flex items-center gap-2 rounded-full bg-[#99CC00] text-[#1F1F1F] px-7 py-3.5 text-sm font-black hover:bg-[#B5E000] transition-colors disabled:opacity-60"
                    >
                      {submitting ? "Submitting..." : "Partner With OneHill"} <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </form>
              ) : (
                <form
                  data-testid="career-form"
                  className="grid grid-cols-1 sm:grid-cols-2 gap-5"
                  onSubmit={onCareerSubmit}
                >
                  <div>
                    <label className={labelCls}>Name *</label>
                    <input data-testid="career-name" required value={careerForm.name} onChange={onCareerChange("name")} className={inputCls} placeholder="Your name" />
                  </div>
                  <div>
                    <label className={labelCls}>Role *</label>
                    <input data-testid="career-role" required value={careerForm.role} onChange={onCareerChange("role")} className={inputCls} placeholder="Role you’re applying for" />
                  </div>
                  <div>
                    <label className={labelCls}>Email *</label>
                    <input data-testid="career-email" required type="email" value={careerForm.email} onChange={onCareerChange("email")} className={inputCls} placeholder="you@email.com" />
                  </div>
                  <div>
                    <label className={labelCls}>Phone *</label>
                    <input data-testid="career-phone" required value={careerForm.phone} onChange={onCareerChange("phone")} className={inputCls} placeholder="+91 — Phone" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>City</label>
                    <input data-testid="career-city" value={careerForm.city} onChange={onCareerChange("city")} className={inputCls} placeholder="Preferred city" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Resume (PDF / DOC / DOCX, max 5MB)</label>
                    <label className="flex items-center gap-3 rounded-2xl border border-dashed border-white/30 hover:border-[#99CC00] transition-colors px-4 py-4 cursor-pointer bg-white/[0.04]">
                      <Upload className="w-4 h-4 text-[#99CC00]" />
                      <span className="text-sm text-white/70 truncate">
                        {resume ? resume.name : "Drop your resume or click to upload"}
                      </span>
                      <input
                        data-testid="career-resume"
                        type="file"
                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        className="hidden"
                        onChange={(e) => setResume(e.target.files?.[0] || null)}
                      />
                    </label>
                    {resume ? (
                      <div className="mt-2 inline-flex items-center gap-2 text-xs text-white/55 font-mono">
                        <FileText className="w-3.5 h-3.5 text-[#99CC00]" />
                        {resume.name} · {(resume.size / 1024).toFixed(0)} KB
                      </div>
                    ) : null}
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Message</label>
                    <textarea data-testid="career-message" rows={4} value={careerForm.message} onChange={onCareerChange("message")} className={inputCls + " resize-none"} placeholder="Why are you a fit for OneHill?" />
                  </div>
                  <div className="sm:col-span-2 flex items-center justify-end gap-3 pt-1">
                    <button
                      type="submit"
                      data-testid="career-submit"
                      disabled={submitting}
                      className="inline-flex items-center gap-2 rounded-full bg-[#99CC00] text-[#1F1F1F] px-7 py-3.5 text-sm font-black hover:bg-[#B5E000] transition-colors disabled:opacity-60"
                    >
                      {submitting ? "Submitting..." : "Submit Application"} <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </Section>
    </div>
  );
}
